import { requiredIntegerMoreThanZero } from "common";
import { produce } from "immer";
import { isNil } from "lodash";
import { AdjustmentOperation, AdjustmentType, DemographicPrice, PricingGroup, PricingTerm } from "types";
import { array, number, object } from "yup";

export const getInitialValues = (
    demographicPrices: DemographicPrice[],
    pricingTerm: PricingTerm,
    tierIndex: number,
) => {
    return produce(pricingTerm, (draft) => {
        draft.demographicPrices = [...demographicPrices];
        if (tierIndex > 0) {
            for (const demographicPrice of draft.demographicPrices) {
                if (!demographicPrice.adjustmentType) {
                    demographicPrice.adjustmentType = AdjustmentType.ABSOLUTE;
                }

                if (!demographicPrice.adjustmentOperation) {
                    demographicPrice.adjustmentOperation = AdjustmentOperation.MARKUP;
                }
            }
        } else {
            draft.quantityMin = 1;
        }
    });
};

export const getValidationSchema = (pricingGroup: PricingGroup, tierIndex: number) => {
    const existingTierMinValues = pricingGroup.terms.map((term) => term.quantityMin);
    const basePrices = pricingGroup.terms[0].demographicPrices;

    return object().shape({
        demographicPrices: array().of(
            object().shape({
                ...(tierIndex === 0
                    ? {
                          price: number().typeError("invalid number").required().min(0, "invalid number"),
                      }
                    : {
                          adjustmentAmount: number()
                              .typeError("invalid number")
                              .required()
                              .min(0, "invalid number")
                              .test("maxValue", "should not be more than base price", (value, context) => {
                                  const { template, adjustmentType, adjustmentOperation } = context.parent;
                                  if (value && adjustmentOperation === AdjustmentOperation.MARKDOWN) {
                                      const baseDemographicPrice = basePrices.find(
                                          (demographicPrice) => demographicPrice.template.id === template.id,
                                      );
                                      if (adjustmentType === AdjustmentType.ABSOLUTE) {
                                          return baseDemographicPrice?.price
                                              ? baseDemographicPrice.price >= value
                                              : true;
                                      }

                                      return value <= 100;
                                  }

                                  return true;
                              })
                              .test("emptyBasePrice", "base price should not be empty", (value, context) => {
                                  const { template } = context.parent;
                                  const baseDemographicPrice = basePrices.find(
                                      (demographicPrice) => demographicPrice.template.id === template.id,
                                  );

                                  return !isNil(baseDemographicPrice?.price);
                              }),
                      }),
            }),
        ),
        ...(tierIndex > 0 && {
            quantityMin: requiredIntegerMoreThanZero()
                .min(1, "should be more than 1")
                .test("checkForExistingTiers", "- Tier with this group size already exists", (value) => {
                    const index = value ? existingTierMinValues.indexOf(value) : -1;
                    return index < 0 || index === tierIndex;
                }),
        }),
    });
};

export const getDemographicPriceMap = (demographicPrices: DemographicPrice[] = []) => {
    const baseDemographicPriceMap: { [key: ID]: number } = {};
    for (const demographicPrice of demographicPrices) {
        baseDemographicPriceMap[demographicPrice.template.id] = demographicPrice.price ?? 0;
    }

    return baseDemographicPriceMap;
};
